import { fetchAccounts } from "@/api/globalApiService";
import { NavbarItems } from "@/components/hoc/Navbar";
import { useGlobalStore } from "@/GlobalStore";
import { buildRoute } from "@/router/constants";
import { Account } from "@/types";
import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useStrictNavigation from "./useStrictNavigation";

const useAccountsList = () => {
  // Use selectors to subscribe only to needed state
  const accountList = useGlobalStore((state) => state.accountList);
  const currentAccountPage = useGlobalStore((state) => state.currentAccountPage);
  const setCurrentAccountPage = useGlobalStore((state) => state.setCurrentAccountPage);
  const setAccountList = useGlobalStore((state) => state.setAccountList);
  const totalAccountPages = useGlobalStore((state) => state.totalAccountPages);
  const setTotalAccountPages = useGlobalStore((state) => state.setTotalAccountPages);
  const setNoAccountsAvailable = useGlobalStore((state) => state.setNoAccountsAvailable);
  const isInitializingAccounts = useGlobalStore((state) => state.isInitializingAccounts);
  const setIsInitializingAccounts = useGlobalStore(
    (state) => state.setIsInitializingAccounts,
  );
  const setSelectedAccount = useGlobalStore((state) => state.setSelectedAccount);
  const setPendingAccount = useGlobalStore((state) => state.setPendingAccount);

  const [searchResults, setSearchResults] = useState<Account[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchPage, setSearchPage] = useState<number>(1);
  const [searchTotalPage, setSearchTotalPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { pathname } = useLocation();
  const strictNavigate = useStrictNavigation();
  const isMountedRef = useRef(true);
  const isCheckingContentRef = useRef(false);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const scrollAbortControllerRef = useRef<AbortController | null>(null);

  const isSearchActive = !!searchQuery;

  const initializeAccounts = useCallback(async () => {
    setIsInitializingAccounts(true);
    try {
      const res = await fetchAccounts({
        platform: "ALL",
        page: 1,
        search: "",
      });
      setTotalAccountPages(res.pagination.total_pages);
      setCurrentAccountPage(1);
      setAccountList(res.results);
    } catch (error) {
      console.error(error);
      if (error instanceof AxiosError && error.response?.status === 404)
        setNoAccountsAvailable(true);
      setAccountList([]);
    } finally {
      setIsInitializingAccounts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNextPage = async () => {
    const hasMorePages = isSearchActive
      ? searchPage < searchTotalPage
      : currentAccountPage < totalAccountPages;
    if (!hasMorePages) return;

    // Cancel previous scroll load request
    if (scrollAbortControllerRef.current) {
      scrollAbortControllerRef.current.abort();
    }

    // Create new abort controller for this load
    scrollAbortControllerRef.current = new AbortController();

    setIsLoading(true);
    try {
      const res = await fetchAccounts({
        platform: "ALL",
        page: searchQuery ? searchPage + 1 : currentAccountPage + 1,
        search: searchQuery,
        signal: scrollAbortControllerRef.current.signal,
      });
      if (isSearchActive) {
        setSearchTotalPage(res.pagination.total_pages);
        setSearchPage((i) => i + 1);
        setSearchResults((prev) => [...prev, ...res.results]);
      } else {
        setTotalAccountPages(res.pagination.total_pages);
        setCurrentAccountPage(currentAccountPage + 1);
        setAccountList([...accountList, ...res.results]);
      }
    } catch (error) {
      if (
        (error as Error)?.name === "CanceledError" ||
        (error as Error)?.name === "AbortError"
      ) {
        // Request was cancelled, don't update state
        return;
      }
      console.error(error);
      if (error instanceof AxiosError && error.response?.status === 404)
        setNoAccountsAvailable(true);
      setAccountList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const checkIfNeedsMoreContent = async (element: HTMLElement, retryCount = 0) => {
    if (
      isLoading ||
      isCheckingContentRef.current ||
      !isMountedRef.current ||
      retryCount > 3
    )
      return;

    const { scrollHeight, clientHeight } = element;
    const hasMorePages = isSearchActive
      ? searchPage < searchTotalPage
      : currentAccountPage < totalAccountPages;

    // If content doesn't fill container and more pages exist, load them
    if (scrollHeight <= clientHeight && hasMorePages) {
      isCheckingContentRef.current = true;
      try {
        await loadNextPage();
        // Check again after a short delay to allow DOM to update
        setTimeout(() => {
          if (!isMountedRef.current) {
            isCheckingContentRef.current = false;
            return;
          }

          const _hasMorePages = isSearchActive
            ? searchPage < searchTotalPage
            : currentAccountPage < totalAccountPages;

          if (!(element.scrollHeight <= element.clientHeight && _hasMorePages)) {
            isCheckingContentRef.current = false;
            return;
          }

          isCheckingContentRef.current = false;
          void checkIfNeedsMoreContent(element, retryCount + 1);
        }, 100);
      } catch (error) {
        isCheckingContentRef.current = false;
        console.error("Error in checkIfNeedsMoreContent:", error);
      }
    }
  };

  const onScrollNextPageLoader = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const element = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = element;

    const hasMorePages = isSearchActive
      ? searchPage < searchTotalPage
      : currentAccountPage < totalAccountPages;

    // Load next page when scrolled near bottom
    if (scrollTop >= scrollHeight - clientHeight - 10 && hasMorePages)
      void loadNextPage();
  };

  // Ref callback to trigger initial check when container is mounted
  const listContainerRefCallback = (element: HTMLDivElement | null) => {
    if (element && !isInitializingAccounts) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const { scrollHeight, clientHeight } = element;
        const hasMorePages = isSearchActive
          ? searchPage < searchTotalPage
          : currentAccountPage < totalAccountPages;

        if (scrollHeight <= clientHeight && hasMorePages) {
          void checkIfNeedsMoreContent(element);
        }
      }, 50);
    }
  };

  const searchAccount = async (search: string) => {
    // Cancel previous search request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    // Create new abort controller for this search
    searchAbortControllerRef.current = new AbortController();

    try {
      const res = await fetchAccounts({
        platform: "ALL",
        page: 1,
        search: search,
        signal: searchAbortControllerRef.current.signal,
      });
      setSearchTotalPage(res.pagination.total_pages);
      setSearchPage(1);
      setSearchResults(res.results);
    } catch (error) {
      if (
        (error as Error)?.name === "CanceledError" ||
        (error as Error)?.name === "AbortError"
      ) {
        // Request was cancelled, don't update state
        return;
      }
      console.error(error);
      if (error instanceof AxiosError && error.response?.status === 404)
        setNoAccountsAvailable(true);
      setAccountList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllAccounts = useCallback(async (): Promise<Account[]> => {
    try {
      // Check if we have pagination info available
      let pageSize = 1000; // Default large page size

      if (totalAccountPages > 0) {
        // Calculate page size based on available pagination info
        // Assuming default page size is 20 (from initializeAccounts)
        const defaultPageSize = 20;
        pageSize = totalAccountPages * defaultPageSize + 1;
      }

      // Fetch all accounts with calculated or default page size
      const allAccountsRes = await fetchAccounts({
        platform: "ALL",
        page: 1,
        search: "",
        pageSize: pageSize,
      });

      return allAccountsRes.results;
    } catch (error) {
      console.error("Error fetching all accounts:", error);
      if (error instanceof AxiosError && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }, [totalAccountPages]);

  const performAccountChange = (account: Account, resetRoute?: boolean) => {
    // Clear stored URLs when switching accounts
    useGlobalStore.getState().clearAllUrls();
    // Proceed with account change if no unsaved changes
    const currentTab = resetRoute
      ? null
      : NavbarItems.find((tab) => pathname.includes(tab.key));
    setSelectedAccount(account);
    switch (currentTab?.key) {
      case "analyze":
        strictNavigate(buildRoute.analyze(account.id));
        break;
      case "research":
        strictNavigate(buildRoute.research(account.id));
        break;
      case "create":
        strictNavigate(buildRoute.create(account.id));
        break;
      case "settings":
        strictNavigate(buildRoute.settings(account.id));
        break;
      case "video-editor":
        strictNavigate(buildRoute.video_editor());
        break;
      default:
        strictNavigate(buildRoute.account(account.id));
        break;
    }
  };

  // useEffect sets selected account immediately
  const handleAccountChange = (account: Account, resetRoute?: boolean) => {
    // Check if there are unsaved changes using the global checker
    const { unsavedChangesChecker } = useGlobalStore.getState();
    if (unsavedChangesChecker && unsavedChangesChecker()) {
      // Set pending account and trigger dialog
      setPendingAccount(account);
      return;
    }
    performAccountChange(account, resetRoute);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Cancel any ongoing requests
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
      if (scrollAbortControllerRef.current) {
        scrollAbortControllerRef.current.abort();
      }
    };
  }, []);

  // search fetch
  useEffect(() => {
    if (!searchQuery) {
      setSearchResults(accountList);
      return;
    }
    setIsLoading(true);
    const timeout = setTimeout(() => void searchAccount(searchQuery), 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return {
    effectiveAccountList: isSearchActive ? searchResults : accountList,
    initializeAccounts,
    isInitializingAccounts,
    searchQuery,
    setSearchQuery,
    searchResults,
    onScrollNextPageLoader,
    listContainerRefCallback,
    isLoading,
    handleAccountChange,
    loadNextPage,
    performAccountChange,
    fetchAllAccounts,
  };
};

export default useAccountsList;
